import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { withAuth } from '../../hoc/Auth';
import { Wrapper, Container, Column } from '../../hoc/Layout';
import Api from '../../Api';
import SecretChat, { ChatList } from '../../Components/SecretChat';

import classes from './styles.css';

/**
 * Секретные чаты со стороны клиента.
 *
 * Разговор может начать любая сторона, поэтому здесь и список уже открытых
 * чатов (включая начатые поддержкой), и кнопка нового. Форма нового чата
 * живёт в отдельном окне: постоянно висящая на экране, она занимала место
 * ровно там, где человек ждёт увидеть переписку.
 */
function SecretChats({ token }) {
    const intl = useIntl();
    const [chats, setChats] = useState([]);
    const [ttlHours, setTtlHours] = useState(24);
    const [activeId, setActiveId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isCreating, setIsCreating] = useState(false);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [subject, setSubject] = useState('');
    const [firstMessage, setFirstMessage] = useState('');
    const [error, setError] = useState(null);

    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const loadChats = useCallback(
        async function () {
            try {
                const data = await new Api({ token }).listSecretChats();
                if (!mountedRef.current) return;
                setChats((data && data.items) || []);
                if (data && data.ttlHours) setTtlHours(data.ttlHours);
                setError(null);
            } catch (e) {
                if (!mountedRef.current) return;
                setError(
                    intl.formatMessage({
                        id: 'SecretChats.error.list',
                        defaultMessage: 'Не удалось загрузить список чатов',
                    })
                );
            } finally {
                if (mountedRef.current) setIsLoading(false);
            }
        },
        [token, intl]
    );

    useEffect(
        function () {
            loadChats();
            // Список перечитывается сам: чат может начать поддержка, и он
            // должен появиться без перезагрузки страницы.
            const timer = setInterval(loadChats, 15000);
            return () => clearInterval(timer);
        },
        [loadChats]
    );

    const loadMessages = useCallback((id) => new Api({ token }).getSecretChatMessages(id), [token]);
    const sendMessage = useCallback(
        (id, body, files) =>
            new Api({ token }).postSecretChatMessage(id, body, files).then((data) => {
                loadChats();
                return data;
            }),
        [token, loadChats]
    );
    // Файл скачивается запросом с токеном: у простой ссылки нет заголовка
    // авторизации, и сервер ответил бы отказом.
    const downloadFile = useCallback(
        (chatId, fileId) => new Api({ token }).downloadSecretChatFile(chatId, fileId, false),
        [token]
    );

    const markRead = useCallback(
        (id) => {
            new Api({ token })
                .readSecretChat(id)
                .then(loadChats)
                .catch(() => {});
        },
        [token, loadChats]
    );

    async function onCreate(e) {
        e.preventDefault();

        const topic = subject.trim();
        if (!topic || isCreating) return;

        setIsCreating(true);
        try {
            const created = await new Api({ token }).createSecretChat(topic, firstMessage.trim() || undefined);
            if (!mountedRef.current) return;
            setSubject('');
            setFirstMessage('');
            setError(null);
            setIsFormOpen(false);
            await loadChats();
            if (created && created.id) setActiveId(created.id);
        } catch (err) {
            if (!mountedRef.current) return;
            setError(intl.formatMessage({ id: 'SecretChats.error.create', defaultMessage: 'Не удалось создать чат' }));
        } finally {
            if (mountedRef.current) setIsCreating(false);
        }
    }

    const active = chats.find((c) => c.id === activeId) || null;

    return (
        <Wrapper>
            <Container>
                <Column width="1180">
                    <div className={classes.Head}>
                        <h1 className={classes.Title}>
                            <FormattedMessage id="SecretChats.title" defaultMessage="Секретные чаты" />
                        </h1>
                        <span className={classes.Subtitle}>
                            <FormattedMessage
                                id="SecretChats.subtitle"
                                defaultMessage="Переписка шифруется и полностью удаляется через {hours} часа"
                                values={{ hours: ttlHours }}
                            />
                        </span>
                    </div>

                    {error ? <div className={classes.Error}>{error}</div> : null}

                    <div className={classes.Board}>
                        <ChatList
                            chats={chats}
                            activeId={activeId}
                            isLoading={isLoading}
                            onPick={setActiveId}
                            onNew={() => setIsFormOpen(true)}
                        />
                        <SecretChat
                            chat={active}
                            side="client"
                            ttlHours={ttlHours}
                            loadMessages={loadMessages}
                            sendMessage={sendMessage}
                            onRead={markRead}
                            downloadFile={downloadFile}
                        />
                    </div>

                    {isFormOpen ? (
                        <div
                            className={classes.Overlay}
                            onClick={(e) => {
                                if (e.target === e.currentTarget) setIsFormOpen(false);
                            }}
                        >
                            <form className={classes.Dialog} onSubmit={onCreate}>
                                <div className={classes.DialogTitle}>
                                    <FormattedMessage id="SecretChat.button.new" defaultMessage="Новый чат" />
                                </div>
                                <input
                                    className={classes.Field}
                                    value={subject}
                                    maxLength={200}
                                    autoFocus
                                    placeholder={intl.formatMessage({
                                        id: 'SecretChats.field.subject',
                                        defaultMessage: 'Тема разговора',
                                    })}
                                    onChange={(e) => setSubject(e.target.value)}
                                />
                                <textarea
                                    className={classes.Field}
                                    value={firstMessage}
                                    rows={3}
                                    maxLength={20000}
                                    placeholder={intl.formatMessage({
                                        id: 'SecretChats.field.first',
                                        defaultMessage: 'Первое сообщение (необязательно)',
                                    })}
                                    onChange={(e) => setFirstMessage(e.target.value)}
                                />
                                <div className={classes.DialogButtons}>
                                    <button
                                        type="button"
                                        className={classes.Cancel}
                                        onClick={() => setIsFormOpen(false)}
                                    >
                                        <FormattedMessage id="SecretChats.button.cancel" defaultMessage="Отмена" />
                                    </button>
                                    <button
                                        type="submit"
                                        className={classes.Create}
                                        disabled={isCreating || !subject.trim()}
                                    >
                                        <FormattedMessage id="SecretChats.button.create" defaultMessage="Начать чат" />
                                    </button>
                                </div>
                            </form>
                        </div>
                    ) : null}
                </Column>
            </Container>
        </Wrapper>
    );
}

export default withAuth(SecretChats);



// WEBPACK FOOTER //
// ./src/PagesOfUser/SecretChats/index.js